import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createTerritoryManager as createTerritoryManagerFn,
  updateTerritoryManager as updateTerritoryManagerService,
  deleteTerritoryManager as deleteTerritoryManagerService,
} from "../services/territory-manager-service";
import { ApiError } from "pages/admin-app/shared/models/response/api-errors";
import { CreateTerritoryManagerResponse } from "../models/response/create-territory-manager-response";
import { CreateTerritoryManagerRequest } from "../models/request/create-territory-manager-request";
import { EditTerritoryManagerResponse } from "../models/response/edit-territory-manager-response";
import { AxiosError } from "axios";
import { EditTerritoryManagerRequest } from "../models/request/edit-territory-manager-request";

interface Props {
  id?: number | string;
  onEditSuccess?: () => void;
  onEditError?: () => void;
  onCreateSuccess?: () => void;
  onCreateError?: () => void;
}

export interface ApiErrorResponse {
  message: string;
  error: string;
  statusCode: number;
}

export default function useMutateTerritoryManager(props?: Props) {
  const queryClient = useQueryClient();

  const {
    mutate: editTerritoryManager,
    error: editTerritoryManagerError,
    isPending: editTerritoryManagerLoading,
    isSuccess: editTerritoryManagerSuccess,
  } = useMutation<
    EditTerritoryManagerResponse,
    AxiosError<ApiErrorResponse>,
    EditTerritoryManagerRequest
  >({
    mutationKey: ["territoryManager", props?.id, "edit"],
    mutationFn: updateTerritoryManagerService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["territoryManager", props?.id],
      });
      queryClient.invalidateQueries({ queryKey: ["territoryManagers"] });
      props?.onEditSuccess && props?.onEditSuccess();
    },
    onError: () => {
      props?.onEditError && props?.onEditError();
    },
  });

  const {
    mutate: deleteTerritoryManager,
    error: deleteTerritoryManagerError,
    isPending: deleteTerritoryManagerLoading,
    reset: deleteTerritoryManagerReset,
    isSuccess: deleteTerritoryManagerSuccess,
    isError: deleteTerritoryManagerIsError,
  } = useMutation({
    mutationKey: ["territoryManager", props?.id, "delete"],
    mutationFn: deleteTerritoryManagerService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["territoryManagers"] });
    },
  });

  const {
    mutate: createTerritoryManager,
    error: createTerritoryManagerError,
    isPending: createTerritoryManagerLoading,
    isSuccess: createTerritoryManagerSuccess,
  } = useMutation<
    CreateTerritoryManagerResponse,
    ApiError,
    CreateTerritoryManagerRequest
  >({
    mutationKey: ["territoryManager", props?.id, "create"],
    mutationFn: createTerritoryManagerFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["territoryManager"] });
      props?.onCreateSuccess && props?.onCreateSuccess();
    },
    onError: () => {
      props?.onCreateError && props?.onCreateError();
    },
  });

  return {
    editTerritoryManager,
    editTerritoryManagerError,
    editTerritoryManagerLoading,
    editTerritoryManagerSuccess,

    deleteTerritoryManager,
    deleteTerritoryManagerError,
    deleteTerritoryManagerLoading,
    deleteTerritoryManagerReset,
    deleteTerritoryManagerSuccess,
    deleteTerritoryManagerIsError,

    createTerritoryManager,
    createTerritoryManagerError,
    createTerritoryManagerLoading,
    createTerritoryManagerSuccess,
  };
}
